import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CreateSparkCampaign } from '../../../../../../service/create-spark-campaign/create-spark-campaign';

/** UI mode for `operating_systems` — maps to TikTok-style ANDROID / IOS. */
export type OperatingSystemsMode = 'ANDROID' | 'IOS';

@Component({
  selector: 'targeting-device',
  imports: [],
  templateUrl: './device.html',
  styleUrl: './device.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Device {
  expanded = signal<boolean>(false);

  readonly createSparkCampaignService = inject(CreateSparkCampaign);

  readonly operatingSystemOptions: { mode: OperatingSystemsMode; label: string }[] = [
    { mode: 'ANDROID', label: 'Android' },
    { mode: 'IOS', label: 'iOS' },
  ];

  selectOperatingSystemsMode(mode: OperatingSystemsMode): void {
    const svc = this.createSparkCampaignService;
    switch (mode) {
      case 'ANDROID':
        svc.operating_systems.set(['ANDROID']);
        break;
      case 'IOS':
        svc.operating_systems.set(['IOS']);
        break;
    }
  }

  isOperatingSystemsModeSelected(mode: OperatingSystemsMode): boolean {
    const v = this.createSparkCampaignService.operating_systems();
    const set = new Set(v);
    switch (mode) {
      case 'ANDROID':
        return set.size === 1 && set.has('ANDROID');
      case 'IOS':
        return set.size === 1 && set.has('IOS');
    }
  }

  operatingSystemSegmentClass(mode: OperatingSystemsMode, index: number): string {
    const selected = this.isOperatingSystemsModeSelected(mode);
    const rounded =
      index === 0
        ? 'rounded-l-md'
        : index === this.operatingSystemOptions.length - 1
          ? 'rounded-r-md'
          : '';
    const overlap = index > 0 ? '-ml-px' : '';
    const base =
      `relative flex min-w-0 flex-1 basis-0 items-center justify-center px-3 py-2 text-sm font-semibold focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${overlap} ${rounded}`;

    if (selected) {
      return `${base} z-10 bg-primary text-white shadow-sm inset-ring-1 inset-ring-primary dark:bg-primary dark:inset-ring-primary`;
    }
    return `${base} bg-white text-gray-900 inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20`;
  }

  operatingSystemsSummaryLabel(): string {
    const v = this.createSparkCampaignService.operating_systems();
    const set = new Set(v);
    if (set.size === 1 && set.has('ANDROID')) {
      return 'Android';
    }
    if (set.size === 1 && set.has('IOS')) {
      return 'iOS';
    }
    if (set.size === 2 && set.has('ANDROID') && set.has('IOS')) {
      return 'Android & iOS';
    }
    return v.join(', ');
  }
}
